import { supabase, testConnection } from "../supabaseClient";

interface BookingData {
  room_id: string;
  check_in: Date;
  check_out: Date;
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  adults: number;
  children: number;
  rooms_count?: number; // Add rooms_count field
  total_price: number;
  special_requests?: string;
  extras_info?: any[];
  payment_info?: {
    method: string;
    status: string;
  };
  hotelSlug?: string; // Add hotelSlug as optional parameter
  // Address fields
  guest_address?: string;
  guest_city?: string;
  guest_zipCode?: string;
  guest_country?: string;
}

export const bookingService = {
  async getRooms(hotelSlug: string) {
    console.log("🔍 Fetching available rooms for hotel:", hotelSlug);

    try {
      // Test connection first
      const isConnected = await testConnection(hotelSlug);
      if (!isConnected) {
        throw new Error("Failed to connect to Supabase");
      }

      // Get hotel ID first
      console.log("🏨 Finding hotel...");
      let hotel;
      
      // First try direct UUID if format matches
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(hotelSlug)) {
        const { data: hotelData, error } = await supabase
          .from("hotels")
          .select("id, name")
          .eq("id", hotelSlug)
          .maybeSingle();
          
        if (!error && hotelData) {
          hotel = hotelData;
        }
      }
      
      // If not found by ID, try by name
      if (!hotel) {
        const { data: hotelData, error } = await supabase
          .from("hotels")
          .select("id, name")
          .ilike("name", `%${hotelSlug}%`)
          .limit(1)
          .maybeSingle();
          
        if (error) {
          console.error("❌ Error finding hotel by name:", error);
        }
        
        if (!hotelData) {
          // Last resort - get first hotel
          const { data: anyHotel, error: anyError } = await supabase
            .from("hotels")
            .select("id, name")
            .limit(1)
            .maybeSingle();
            
          if (anyError) {
            console.error("❌ Error finding any hotel:", anyError);
            throw new Error("No hotels found");
          }
          
          if (!anyHotel) {
            throw new Error("No hotels found in database");
          }
          
          hotel = anyHotel;
        } else {
          hotel = hotelData;
        }
      }

      console.log("🏨 Using hotel:", hotel?.name);

      // Then get rooms for this hotel
      const { data, error } = await supabase
        .from("room_types")
        .select(
          "id, name, code, description, max_occupancy, base_rate, bookable"
        )
        .eq("hotel_id", hotel.id)
        .eq("bookable", true);

      if (error) {
        console.error("❌ Error fetching rooms:", error);
        throw error;
      }

      console.log("✅ Rooms fetched successfully:", data);
      return data || [];
    } catch (error) {
      console.error("❌ getRooms failed:", error);
      throw error;
    }
  },

  async createBooking(bookingData: BookingData) {
    console.log("📝 Creating new booking...");
    console.log("Booking data:", {
      ...bookingData,
      guest_email: "***@***.***", // Mask email in logs
    });

    const hotelSlug = bookingData.hotelSlug || 'Hotel Adriatica'; // Use provided hotelSlug or fallback

    try {
      // Validate required fields first
      if (!bookingData.room_id) {
        throw new Error("Room ID is required");
      }

      console.log("🔍 Validating room ID:", bookingData.room_id);

      // 1. Test connection
      const isConnected = await testConnection(hotelSlug);
      if (!isConnected) {
        throw new Error("Failed to connect to Supabase");
      }

      // 2. Get hotel ID
      console.log("🏨 Finding hotel...");
      // Try by name first since there's no slug field in your schema
      let hotel;
      
      // First try direct UUID if format matches
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(hotelSlug)) {
        const { data: hotelData, error } = await supabase
          .from("hotels")
          .select("id, name")
          .eq("id", hotelSlug)
          .maybeSingle();
          
        if (!error && hotelData) {
          hotel = hotelData;
        }
      }
      
      // If not found by ID, try by name
      if (!hotel) {
        const { data: hotelData, error } = await supabase
          .from("hotels")
          .select("id, name")
          .ilike("name", `%${hotelSlug}%`)
          .limit(1)
          .maybeSingle();
          
        if (error) {
          console.error("❌ Error finding hotel by name:", error);
        }
        
        if (!hotelData) {
          // Last resort - get first hotel
          const { data: anyHotel, error: anyError } = await supabase
            .from("hotels")
            .select("id, name")
            .limit(1)
            .maybeSingle();
            
          if (anyError) {
            console.error("❌ Error finding any hotel:", anyError);
            throw new Error("No hotels found");
          }
          
          if (!anyHotel) {
            throw new Error("No hotels found in database");
          }
          
          hotel = anyHotel;
        } else {
          hotel = hotelData;
        }
      }

      if (!hotel) {
        throw new Error("Could not find any hotel to assign booking to");
      }

      console.log("🏨 Using hotel:", hotel.name);

      // 3. Validate room type exists and is bookable
      console.log("🏠 Validating room type:", bookingData.room_id);
      const { data: roomType, error: roomError } = await supabase
        .from("room_types")
        .select("*")
        .eq("id", bookingData.room_id)
        .eq("hotel_id", hotel.id)
        .eq("bookable", true)
        .maybeSingle();

      if (roomError) {
        console.error("❌ Error validating room type:", roomError);
        throw new Error(`Error validating room type: ${roomError.message}`);
      }

      if (!roomType) {
        throw new Error(`Room type not found or not bookable. Room ID: ${bookingData.room_id}`);
      }

      console.log("✅ Room type validated:", roomType);

      // Calculate nights and room rate
      const checkInDate = new Date(bookingData.check_in).getTime();
      const checkOutDate = new Date(bookingData.check_out).getTime();
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      const roomRate = roomType.price_per_night || (bookingData.total_price / nights);

      // 3. Create or find guest record
      console.log("👤 Checking if guest exists...");
      let guest;

      const { data: existingGuests, error: guestCheckError } = await supabase
        .from("guests")
        .select("*")
        .eq("email", bookingData.guest_email);

      if (guestCheckError) {
        console.error("❌ Error checking for existing guest:", guestCheckError);
        throw guestCheckError;
      }

      if (existingGuests && existingGuests.length > 0) {
        console.log("✅ Existing guest found:", existingGuests[0]);
        guest = existingGuests[0];
        
        // Check if existing guest has missing information and update if needed
        const existingGuest = existingGuests[0];
        const needsUpdate = 
          (!existingGuest.first_name && bookingData.guest_name.split(" ")[0]) ||
          (!existingGuest.last_name && bookingData.guest_name.split(" ").slice(1).join(" ")) ||
          (!existingGuest.phone && bookingData.guest_phone) ||
          (!existingGuest.address_street && bookingData.guest_address) ||
          (!existingGuest.address_city && bookingData.guest_city) ||
          (!existingGuest.address_postal_code && bookingData.guest_zipCode) ||
          (!existingGuest.address_country && bookingData.guest_country);
        
        if (needsUpdate) {
          console.log("🔄 Updating existing guest with missing information...");
          
          // First update the guest
          const { error: updateError } = await supabase
            .from("guests")
            .update({
              first_name: existingGuest.first_name || bookingData.guest_name.split(" ")[0],
              last_name: existingGuest.last_name || bookingData.guest_name.split(" ").slice(1).join(" "),
              phone: existingGuest.phone || bookingData.guest_phone,
              address_street: existingGuest.address_street || bookingData.guest_address,
              address_city: existingGuest.address_city || bookingData.guest_city,
              address_postal_code: existingGuest.address_postal_code || bookingData.guest_zipCode,
              address_country: existingGuest.address_country || bookingData.guest_country,
            })
            .eq("id", existingGuest.id);
            
          if (updateError) {
            console.error("❌ Error updating existing guest:", updateError);
            // Continue with original guest data
          } else {
            // Fetch the updated guest data
            const { data: updatedGuest, error: fetchError } = await supabase
              .from("guests")
              .select("*")
              .eq("id", existingGuest.id)
              .maybeSingle();
              
            if (fetchError) {
              console.error("❌ Error fetching updated guest:", fetchError);
              // Continue with original guest data
            } else if (updatedGuest) {
              console.log("✅ Guest updated successfully:", updatedGuest);
              guest = updatedGuest;
            }
          }
        }
      } else {
        console.log("👤 Creating new guest...");
        const { data: newGuest, error: guestError } = await supabase
          .from("guests")
          .insert({
            first_name: bookingData.guest_name.split(" ")[0],
            last_name: bookingData.guest_name.split(" ").slice(1).join(" "),
            email: bookingData.guest_email,
            phone: bookingData.guest_phone,
            address_street: bookingData.guest_address || null,
            address_city: bookingData.guest_city || null,
            address_postal_code: bookingData.guest_zipCode || null,
            address_country: bookingData.guest_country || null,
          })
          .select()
          .maybeSingle();

        if (guestError) {
          console.error("❌ Error creating guest:", guestError);
          throw guestError;
        }

        if (!newGuest) {
          throw new Error("Failed to create guest record");
        }

        guest = newGuest;
        console.log("✅ New guest created:", guest);
      }

      // 4. Create booking record
      console.log("📅 Creating booking record...");
      const confirmationNumber = Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase();

      console.log("📊 Booking calculation:", {
        nights,
        totalPrice: bookingData.total_price,
        roomRate: bookingData.total_price / nights,
        roomTypeId: roomType.id
      });

      const { data: booking, error: bookingError } = await supabase
        .from("reservations")
        .insert({
          hotel_id: hotel.id,
          room_type_id: roomType.id, // This is critical - verified above
          guest_id: guest.id,
          confirmation_number: confirmationNumber,
          status: "confirmed",
          check_in_date: bookingData.check_in.toISOString().split("T")[0],  // Match your database columns
          check_out_date: bookingData.check_out.toISOString().split("T")[0], // Match your database columns
          adults: bookingData.adults,
          children: bookingData.children,
          rooms_count: bookingData.rooms_count || 1,
          room_rate: roomRate.toString(),
          total_room_cost: (roomRate * nights * (bookingData.rooms_count || 1)).toString(),
          taxes: "0.00",
          fees: "0.00",
          total_amount: bookingData.total_price,
          commission_rate: "3.0000",
          commission_amount: (bookingData.total_price * 0.03).toString(),
          commission_status: "pending",
          payment_status: "pending",
          payment_method: bookingData.payment_info?.method || "online",
          oracle_synced: false,
          email_sent: false,
          special_requests: bookingData.special_requests || "",
          booking_source: "hotelbuzz",
          metadata: JSON.stringify({
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server',
            extras_info: bookingData.extras_info || []
          }),
        })
        .select()
        .maybeSingle();

      if (bookingError) {
        console.error("❌ Error creating booking:", bookingError);
        throw bookingError;
      }

      if (!booking) {
        throw new Error("Failed to create booking record");
      }

      console.log("✅ Booking created successfully:", {
        id: booking.id,
        confirmation: booking.confirmation_number,
        nights: booking.nights,
        total: booking.total_amount,
      });

      return booking;
    } catch (error) {
      console.error("❌ createBooking failed:", error);
      throw error;
    }
  },
};
